<script setup>
import { computed, ref, watchEffect } from "vue";

// define component props for the slider component
const { min, max, step, minValue, maxValue, currency } = defineProps({
  min: {
    type: Number,
    default: 0,
  },
  max: {
    type: Number,
    default: 100,
  },
  step: {
    type: Number,
    default: 1,
  },
  minValue: {
    type: Number,
    default: 50,
  },
  maxValue: {
    type: Number,
    default: 80,
  },
  currency: {
    type: String,
    default: "",
  },
});

// define emits for the slider component
const emit = defineEmits(["update:minValue", "update:maxValue"]);

// define refs for the slider element and the slider values
const slider = ref(null);
const inputMin = ref(null);
const inputMax = ref(null);
const sliderMinValue = ref(minValue);
const sliderMaxValue = ref(maxValue);

// function to get the percentage of a value between the min and max values
const getPercent = (value, min, max) => {
  return ((value - min) / (max - min)) * 100;
};

// function to get the difference between the min and max values
const sliderDifference = computed(() => {
  return Math.abs(sliderMaxValue.value - sliderMinValue.value);
});

// function to set the css variables for width, left and right
const setCSSProps = (left, right) => {
  slider.value.style.setProperty("--progressLeft", `${left}%`);
  slider.value.style.setProperty("--progressRight", `${right}%`);
};

// watchEffect to emit the updated values, and update the css variables
// when the slider values change
watchEffect(() => {
  if (slider.value) {
    // emit slidet values when updated
    emit("update:minValue", sliderMinValue.value);
    emit("update:maxValue", sliderMaxValue.value);

    // calculate values in percentages
    const leftPercent = getPercent(sliderMinValue.value, min, max);
    const rightPercent = 100 - getPercent(sliderMaxValue.value, min, max);

    // set the CSS variables
    setCSSProps(leftPercent, rightPercent);
  }
});

// validation sliderMinValue do not greater than sliderMaxValue and opposite
const onInput = ({ target }) => {
  if (target.name === "min") {
    target.value > sliderMaxValue.value
      ? (target.value = sliderMaxValue.value)
      : (sliderMinValue.value = parseFloat(target.value));
  }

  if (target.name === "max") {
    target.value < sliderMinValue.value
      ? (target.value = sliderMinValue.value)
      : (sliderMaxValue.value = parseFloat(target.value));
  }
};
</script>
<template>
  <div class="range-slider-element">
    <div ref="slider" class="custom-slider minmax bg-custom-grey">
      <div class="minmax-indicator">
        <div class="progress bg-dark-bg"></div>
      </div>
      <input
        ref="inputMin"
        type="range"
        name="min"
        id="min"
        :min="min"
        :max="max"
        :value="minValue"
        :step="step"
        @input="onInput"
      />
      <input
        ref="inputMax"
        type="range"
        name="max"
        id="max"
        :min="min"
        :max="max"
        :value="maxValue"
        :step="step"
        @input="onInput"
      />
    </div>
    <div
      class="minmax-inputs mt-5 flex items-center justify-between font-bold text-sm text-dark-bg"
    >
      <div class="min-value">
        <span class="mr-0">CHF</span>
        {{ sliderMinValue }}
      </div>
      <div class="max-value">
        <span class="mr-0">CHF</span>
        {{ sliderMaxValue }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.slider-cont {
  width: 100%;
}
.custom-slider {
  --trackHeight: 4px;
  --thumbRadius: 12px;
}
.custom-slider input[type="range"] {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background: none;
  border-radius: 999px;
  height: 100%;
  pointer-events: none;
  position: relative;
  z-index: 0;
}
.custom-slider input[type="range"]:before {
  content: "";
  display: block;
  height: 100%;
  position: absolute;
  width: var(--ProgressPercent, 100%);
  --tw-bg-opacity: 1;
  background-color: rgb(11 11 11 / var(--un-bg-opacity));
  border-radius: 999px;
}
.custom-slider input[type="range"]::-webkit-slider-runnable-track {
  -webkit-appearance: none;
  appearance: none;
  --tw-bg-opacity: 1;
  background-color: rgb(11 11 11 / var(--un-bg-opacity));
  border-radius: 999px;
  height: var(--trackHeight);
}
.custom-slider input[type="range"]::-webkit-slider-thumb {
  height: var(--thumbRadius);
  margin-top: calc((var(--trackHeight) - var(--thumbRadius)) / 2);
  position: relative;
  width: var(--thumbRadius);
  --tw-bg-opacity: 1;
  background-color: rgb(11 11 11 / var(--un-bg-opacity));
  border: 1px solid rgb(11 11 11 / var(--un-bg-opacity));
  --tw-border-opacity: 1;
  -webkit-appearance: none;
  appearance: none;
  border-radius: 100%;
  pointer-events: all;
  cursor: grab;
  z-index: 1;
}
.custom-slider.default input[type="range"]::-moz-range-track {
  -moz-appearance: none;
  appearance: none;
  --tw-bg-opacity: 1;
  background-color: rgb(11 11 11 / var(--un-bg-opacity));
  border-radius: 999px;
  height: var(--trackHeight);
}
.custom-slider input[type="range"]::-moz-range-thumb {
  box-sizing: border-box;
  height: var(--thumbRadius);
  margin-top: calc((var(--trackHeight) - var(--thumbRadius)) / 2);
  position: relative;
  width: var(--thumbRadius);
  --tw-bg-opacity: 1;
  background-color: rgb(11 11 11 / var(--un-bg-opacity));
  border: 1px solid rgb(11 11 11 / var(--un-bg-opacity));
  --tw-border-opacity: 1;
  -moz-appearance: none;
  appearance: none;
  border-radius: 100%;
  pointer-events: all;
  cursor: grab;
  z-index: 1;
}
.custom-slider.minmax {
  height: var(--trackHeight);
  position: relative;
  --tw-bg-opacity: 1;
  border-radius: 0;
  margin: 0.5rem 0;
  --progressLeft: 0%;
  --progressRight: 0%;
}
.custom-slider .minmax-indicator {
  height: 100%;
  left: var(--thumbRadius);
  pointer-events: none;
  position: absolute;
  right: var(--thumbRadius);
}
.custom-slider .minmax-indicator .progress {
  content: "";
  position: absolute;
  --tw-bg-opacity: 1;
  height: 100%;
  left: var(--progressLeft);
  right: var(--progressRight);
}
.custom-slider.minmax input[type="range"] {
  position: absolute;
  width: calc(100% - var(--thumbRadius));
}
.custom-slider.minmax input[type="range"][name="max"] {
  left: var(--thumbRadius);
}
.custom-slider.minmax input[type="range"]::-webkit-slider-runnable-track {
  background: none;
}
.custom-slider.minmax input[type="range"]:before {
  display: none;
}
.minmax-inputs {
  display: flex;
  justify-content: space-between;
}
</style>
